import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import Register from './components/Register'; 
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import DashboardHome from './components/DashboardHome';
import CreateCategory from './components/CreateCategory';
import CreateThemes from './components/CreateThemes';
import CreateContents from './components/CreateContents';
import ThemeSearcher from './components/ThemeSearcher';
import ContentSearcher from './components/ContentSearch';
import ReloadWallet from './components/ReloadWallet';
import OrderWallet from './components/OrderWallet';
import Pay from './components/Pay';
import CheckBalance from './components/CheckBalance';
import UserList from './components/UserList';
import UserProfile from './components/UserProfile';
import PublicContentPage from './components/PublicContentPage';
import GoogleSuccess from './components/GoogleSuccess';
import StoreList from './components/StoreList';

const ProtectedRoute = ({ isLoggedIn, requiredRole, role }) => {
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  if (requiredRole && role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [role, setRole] = useState('');
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');

  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');

  const [userId, setUserId] = useState('');
  const [document, setDocument] = useState('');
  const [celular, setCelular] = useState('');
  const [email, setEmail] = useState('');
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      try {
        const payload = JSON.parse(atob(savedToken.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          setIsLoggedIn(true);
          setToken(savedToken);
          setRole(payload.role || '');
          setUsername(payload.username || '');
          setUserId(payload.userId || payload.id || payload._id || '');
          setNombre(payload.nombre || '');
          setApellido(payload.apellido || '');
          setDocument(payload.document || '');
          setCelular(payload.celular || '');
          setEmail(payload.email || '');
        } else {
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Error al leer el token:', error);
        localStorage.removeItem('token');
      }
    }
    setAuthReady(true);
  }, []);

  useEffect(() => {
    try {
      const root = window.document.documentElement;
      if (root) {
        root.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
      }
    } catch (error) {
      console.error("Error al cambiar el tema:", error);
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const handleLoginSuccess = (data) => {
    localStorage.setItem('token', data.token);
    setIsLoggedIn(true);
    setToken(data.token);

    // Verificamos si los datos vienen de Google (planos) o del Login normal (anidados en .user)
    const user = data.user ? data.user : data;

    setRole(user.role || 'Usuario');
    setUsername(user.username);
    setNombre(user.nombre);
    setApellido(user.apellido);
    setUserId(user.id || user.userId || user._id || '');
    setDocument(user.document || '');
    setCelular(user.celular || '');
    setEmail(user.email || '');
  };

  if (!authReady) return null;

  const dashboardProps = {
    toggleTheme,
    theme,
    isLoggedIn,
    role: isLoggedIn ? role : 'Usuario',
    token,
    username,
    nombre,
    apellido,
    userId,
    document,
    celular,
    email,
    onUsernameUpdate: setUsername,
  };

  return (
    <Router>
      <Routes>

        {/* Autenticacion */}
        <Route path="/login" element={
          isLoggedIn ? <Navigate to="/dashboard" /> :
          <Login onSuccess={handleLoginSuccess} toggleTheme={toggleTheme} theme={theme} />
        } />

        <Route path="/register" element={
          <Register theme={theme} toggleTheme={toggleTheme} />
        } />

        <Route path="/auth/google/success" element={
          <GoogleSuccess onSuccess={handleLoginSuccess} />
        } />

        {/* RUTA PUBLICA DE CONTENIDO — fuera del dashboard para que /content/:id funcione */}
        <Route path="/content/:id" element={
          <PublicContentPage theme={theme} toggleTheme={toggleTheme} />
        } />

        <Route path="/creador/:slug" element={<Dashboard {...dashboardProps} />}>
          <Route index element={
            <DashboardHome
              toggleTheme={toggleTheme}
              theme={theme}
              isLoggedIn={isLoggedIn}
              role={role}
              token={token}
              username={username}
              userId={userId}
              nombre={nombre}
              apellido={apellido}
            />
          } />

          <Route path="stores" element={<StoreList theme={theme} />} />

          <Route path="search-theme" element={
            <ThemeSearcher token={token} theme={theme} role={role} isLoggedIn={isLoggedIn} />
          } />

          <Route element={<ProtectedRoute isLoggedIn={isLoggedIn} role={role} />}>
            <Route path="create-contents" element={<CreateContents token={token} theme={theme} />} />
            <Route path="edit-content/:contentId" element={<CreateContents token={token} theme={theme} />} />
            <Route path="users/:id" element={
              <UserProfile
                token={token}
                theme={theme}
                currentUserId={userId}
                currentUserRole={role}
                onUsernameUpdate={setUsername}
              />
            } />
          </Route>
        </Route>

        {/* Dashboard principal */}
        <Route path="/dashboard" element={<Dashboard {...dashboardProps} />}>

          <Route index element={
            <DashboardHome
              toggleTheme={toggleTheme}
              theme={theme}
              isLoggedIn={isLoggedIn}
              role={role}
              token={token}
              username={username}
              userId={userId}
              nombre={nombre}
              apellido={apellido}
            />
          } />

          <Route path="stores" element={<StoreList theme={theme} />} />

          {/* Rutas publicas del dashboard */}
          <Route path="search-theme" element={
            <ThemeSearcher token={token} theme={theme} role={role} isLoggedIn={isLoggedIn} />
          } />
          <Route path="search-content" element={
            <ContentSearcher token={token} theme={theme} />
          } />

          {/* Rutas que requieren login */}
          <Route element={<ProtectedRoute isLoggedIn={isLoggedIn} role={role} />}>
            <Route path="create-contents" element={<CreateContents token={token} theme={theme} />} />
            <Route path="edit-content/:contentId" element={<CreateContents token={token} theme={theme} />} />
            <Route path="reload-wallet" element={<ReloadWallet />} />
            <Route path="order-wallet" element={<OrderWallet />} />
            <Route path="pay" element={<Pay />} />
            <Route path="check-balance" element={<CheckBalance />} />
            <Route path="users/:id" element={
              <UserProfile
                token={token}
                theme={theme}
                currentUserId={userId}
                currentUserRole={role}
                onUsernameUpdate={setUsername}
              />
            } />
          </Route>

          {/* Rutas que requieren Admin */}
          <Route element={<ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="Admin" role={role} />}>
            <Route path="create-category" element={<CreateCategory token={token} theme={theme} />} />
            <Route path="create-themes" element={<CreateThemes token={token} theme={theme} isLoggedIn={isLoggedIn} />} />
            <Route path="users" element={<UserList token={token} theme={theme} />} />
            <Route path="stores" element={<StoreList theme={theme} />} />
          </Route>

        </Route>

        {/* Ruta por defecto */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />

      </Routes>
    </Router>
  );
}

export default App;